import type { VenuePartnerSignup } from "@/lib/database/client/database";
import { PARTNER_TYPE_COPY, PartnerType } from "@/lib/constants/venue-partner-types";

/** Values the /partners sign-up form collects. */
export interface PartnerFormValues {
  fullName: string;
  email: string;
  role: string;
  /** Empty until the person picks an answer to "What best describes you?". */
  partnerType: PartnerType | "";
  /** Answer to "Please specify". Asked only when the partner type is "other". */
  partnerTypeOther: string;
  propertyName: string;
  propertyWebsite: string;
  /** Optional answer to "How did you hear about us?". */
  referralSource: string;
}

/** Per-field error messages. A missing key means the field is valid. */
export type PartnerFormErrors = Partial<Record<keyof PartnerFormValues, string>>;

/** Empty form state, used on first render and after a successful submit. */
export const EMPTY_PARTNER_FORM: PartnerFormValues = {
  fullName: "",
  email: "",
  role: "",
  partnerType: "",
  partnerTypeOther: "",
  propertyName: "",
  propertyWebsite: "",
  referralSource: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Accepts "hotel.com", "www.hotel.com" and "https://hotel.com/path". Venues rarely type the protocol.
const WEBSITE_PATTERN = /^(https?:\/\/)?[^\s/.]+(\.[^\s/.]+)+(\/\S*)?$/i;

/**
 * Validates the partner sign-up form. The name and website fields are hidden until a
 * partner type is picked, so they are checked only after that.
 *
 * @param values - The current form values.
 * @returns An error message for each invalid field. An empty object means the form is valid.
 */
export function validatePartnerForm(values: PartnerFormValues): PartnerFormErrors {
  const errors: PartnerFormErrors = {};

  if (!values.fullName.trim()) errors.fullName = "Your name is required.";

  if (!values.email.trim()) errors.email = "Your email is required.";
  else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email.";

  if (!values.role.trim()) errors.role = "Your role is required.";

  if (!values.partnerType) {
    errors.partnerType = "Please choose what best describes you.";
    return errors;
  }

  if (visiblePartnerFields(values).includes("partnerTypeOther") && !values.partnerTypeOther.trim())
    errors.partnerTypeOther = "Please tell us what you are.";

  const { noun, websitePlaceholder } = PARTNER_TYPE_COPY[values.partnerType];

  if (!values.propertyName.trim()) errors.propertyName = `${noun} name is required.`;

  if (!values.propertyWebsite.trim()) errors.propertyWebsite = `${noun} website is required.`;
  else if (!WEBSITE_PATTERN.test(values.propertyWebsite.trim()))
    errors.propertyWebsite = `Please enter a valid website, like ${websitePlaceholder}.`;

  return errors;
}

/**
 * Lists the fields the form shows for the current values. The name and website fields
 * appear once a partner type is picked, and "Please specify" appears only for "other".
 *
 * @param values - The current form values.
 * @returns The keys of the visible fields.
 */
export function visiblePartnerFields(values: PartnerFormValues): Array<keyof PartnerFormValues> {
  const fields: Array<keyof PartnerFormValues> = ["fullName", "email", "role", "partnerType", "referralSource"];
  if (values.partnerType === "other") fields.push("partnerTypeOther");
  if (values.partnerType) fields.push("propertyName", "propertyWebsite");
  return fields;
}

/**
 * Turns valid form values into the sign-up the database call saves. Trims every field so
 * stray spaces do not reach the database or Slack, and drops "Please specify" unless the
 * partner type is "other".
 *
 * @param values - Form values that passed {@link validatePartnerForm}.
 * @returns The sign-up to save.
 * @throws When no partner type is picked, which validation already prevents.
 */
export function toPartnerSignup(values: PartnerFormValues): VenuePartnerSignup {
  if (!values.partnerType) throw new Error("toPartnerSignup: partner type is required");

  return {
    fullName: values.fullName.trim(),
    email: values.email.trim(),
    role: values.role.trim(),
    partnerType: values.partnerType,
    partnerTypeOther: values.partnerType === "other" ? values.partnerTypeOther.trim() : "",
    propertyName: values.propertyName.trim(),
    propertyWebsite: values.propertyWebsite.trim(),
    referralSource: values.referralSource.trim(),
  };
}
